import { PaletteMode } from '@mui/material'
import { createTheme } from '@mui/material/styles'
import { merge } from 'lodash'
import { DefaultThemeValues } from '../expanded-theme'

export const defaultLightThemeValues: DefaultThemeValues = {
  palette: {
    brand: {
      50: '#EFF6FF',
      100: '#DBEAFE',
      200: '#BFDBFE',
      300: '#93C5FD',
      400: '#60A5FA',
      500: '#3B82F6',
      600: '#2563EB',
      700: '#1D4ED8',
      800: '#1E40AF',
      900: '#1E3A8A'
    },
    customBlue: {
      50: '#E8F0FE',
      100: '#B9D6FA',
      200: '#89BBF7',
      300: '#5AA0F3',
      400: '#2A85F0',
      500: '#1C47CB',
      600: '#183B9C',
      700: '#14306D',
      800: '#10253E',
      900: '#0B1A0F'
    },
    customViolet: {
      50: '#EFEFFF',
      100: '#D6D6FF',
      200: '#CED0FF',
      300: '#BDC0FF',
      400: '#A3A8FF',
      500: '#7C81FF',
      600: '#5B62FF',
      700: '#4346CC',
      800: '#2B2B99',
      900: '#181866'
    },
    customGrey: {
      50: '#F8FAFC',
      100: '#F1F5F9',
      200: '#DFDFDF',
      300: '#CBD5E1',
      400: '#94A3B8',
      500: '#5C6D8E',
      600: '#475569',
      700: '#334155',
      800: '#1E293B',
      900: '#0F172A'
    },
    customError: {
      50: '#FFF1F2',
      100: '#FFE4E6',
      200: '#FECDD3',
      300: '#FDA4AF',
      400: '#FB7185',
      500: '#F43F5E',
      600: '#E11D48',
      700: '#BE123C',
      800: '#9F1239',
      900: '#881337'
    },
    customSuccess: {
      50: '#ECFDF5',
      100: '#D1FAE5',
      200: '#A7F3D0',
      300: '#6EE7B7',
      400: '#34D399',
      500: '#10B981',
      600: '#059669',
      700: '#047857',
      800: '#065F46',
      900: '#064E3B'
    },
    customYellow: {
      50: '#FEF0C7',
      100: '#FEF0C7',
      200: '#FEDF89',
      300: '#FEDF89',
      400: '#FEDF89',
      500: '#FDB022',
      600: '#F79009',
      700: '#F79009',
      800: '#F79009',
      900: '#F79009'
    },
    groupsyPink: {
      50: '#FBEBFF',
      100: '#F4CCFF',
      200: '#EFB2FF',
      300: '#EFB2FF',
      400: '#EFB2FF',
      500: '#E78CFF',
      600: '#DB54FF',
      700: '#BA09B7',
      800: '#A008A0',
      900: '#A008A0'
    },
    customBlack: {
      50: '#F8F9FA',
      100: '#E9ECEF',
      200: '#DEE2E6',
      300: '#303F44',
      400: '#7589B0',
      500: '#5C6D8E',
      600: '#495057',
      700: '#343A40',
      800: '#29344A',
      900: '#101828'
    }
  }
}

const theme = createTheme(
  merge(defaultLightThemeValues, {
    palette: {
      mode: 'light' as PaletteMode,
      'text.disabled': '#808080',
      primary: {
        main: '#1C47CB',
        contrastText: '#fff',
        highlight: '#E8EDFA'
      },
      secondary: {
        main: '#344054',
        contrastText: '#fff'
      },
      groupsy: {
        main: '#DB54FF',
        gradient: 'linear-gradient(107deg, #DB54FF 5%, #BA09B7 43.39%)',
        gradientHover: 'linear-gradient(107deg, #BA09B7 5%, #DB54FF 43.39%)'
      },
      groupsy700: {
        main: '#BA09B7'
      },
      groupsyPink: defaultLightThemeValues.palette.groupsyPink,
      warning: {
        main: '#FEC84B',
        contrastText: '#fff'
      },
      error: {
        main: '#DC362E',
        contrastText: '#fff'
      }
    },
    components: {
      MuiAccordionDetails: {
        styleOverrides: {
          root: {
            padding: '0 !important'
          }
        }
      },
      MuiAccordionSummary: {
        styleOverrides: {
          root: {
            '&.Mui-expanded': {
              alignItems: 'center'
            },
            '&.Mui-content': {
              alignItems: 'center'
            }
          }
        }
      },
      MuiLinearProgress: {
        styleOverrides: {
          barColorPrimary: {
            backgroundColor: `#1C47CB !important`
          },
          colorPrimary: {
            backgroundColor: '#dfdfdf !important',
            opacity: 0.7
          },
          barColorSecondary: {
            backgroundColor: `#344054 !important`
          },
          colorSecondary: {
            backgroundColor: '#dfdfdf !important',
            opacity: 0.7
          }
        }
      },
      MuiCardActions: {
        styleOverrides: {
          root: {
            padding: 12,
            justifyContent: 'flex-end'
          }
        }
      },
      MuiButton: {
        styleOverrides: {
          root: {
            textTransform: 'none' as const
          }
        }
      },
      MuiTypography: {
        defaultProps: {
          variantMapping: {
            'pb.display.large': 'h3',
            'pb.display.medium': 'h3',
            'pb.display.small': 'h3',
            'pb.headline.large': 'h4',
            'pb.headline.medium': 'h4',
            'pb.body.large': 'p',
            'pb.body.medium': 'p',
            'pb.body.small': 'p',
            'pb.body.responsive': 'p',
            'pb.title.small': 'h5',
            'pb.title.large': 'h5',
            'pb.label.small': 'p'
          }
        }
      }
    },
    typography: {
      fontFamily: '"Outfit", Arial, sans-serif',
      'pb.display.large': {
        fontWeight: 800,
        fontSize: '36px',
        lineHeight: '100%',
        letterSpacing: '0%',
        textAlign: 'center' as 'center'
      },
      'pb.display.medium': {
        fontWeight: 800,
        fontSize: '24px',
        lineHeight: '100%',
        letterSpacing: '0%',
        textAlign: 'center' as 'center'
      },
      'pb.display.small': {
        fontWeight: 800,
        fontSize: '18px',
        lineHeight: '100%',
        letterSpacing: '0%',
        textAlign: 'center' as 'center'
      },
      'pb.headline.large': {
        fontFamily: '"Outfit", Arial, sans-serif',
        fontWeight: 800,
        fontSize: '16px',
        lineHeight: '100%',
        letterSpacing: '0%',
        textAlign: 'center' as 'center'
      },
      'pb.headline.medium': {
        fontFamily: '"Outfit", Arial, sans-serif',
        fontWeight: 800,
        fontSize: '14px',
        lineHeight: '100%',
        letterSpacing: '0%',
        textAlign: 'center' as 'center'
      },
      'pb.body.large': {
        fontWeight: 400,
        fontSize: '18px',
        lineHeight: '100%',
        letterSpacing: '0%',
        textAlign: 'center' as 'center',
        textDecoration: 'underline' as 'underline',
        textDecorationStyle: 'solid' as 'solid',
        textDecorationOffset: '0%',
        textDecorationThickness: '0%',
        textDecorationSkipInk: 'auto' as 'auto'
      },
      'pb.body.medium': {
        fontWeight: 400,
        fontSize: '16px',
        lineHeight: '100%',
        letterSpacing: '0%',
        textAlign: 'center' as 'center',
        textDecoration: 'underline' as 'underline',
        textDecorationStyle: 'solid' as 'solid',
        textDecorationOffset: '0%',
        textDecorationThickness: '0%',
        textDecorationSkipInk: 'auto' as 'auto'
      },
      'pb.body.small': {
        fontFamily: '"Outfit", Arial, sans-serif',
        fontWeight: 400,
        fontSize: '14px',
        lineHeight: '100%',
        letterSpacing: '0%',
        textAlign: 'center' as 'center'
      },
      'pb.body.responsive': {
        fontFamily: '"Outfit", Arial, sans-serif',
        fontWeight: 400,
        fontSize: '14px', // default to small
        lineHeight: '100%',
        letterSpacing: '0%',
        textAlign: 'center' as 'center',
        '@media (min-width:600px)': {
          fontSize: '16px' // medium for tablets
        },
        '@media (min-width:900px)': {
          fontSize: '18px' // large for desktops
        }
      },
      'pb.title.small': {
        fontWeight: 600,
        fontStyle: 'normal',
        fontSize: '14px',
        lineHeight: '100%',
        letterSpacing: '0%',
        verticalAlign: 'middle'
      },
      'pb.title.large': {
        fontWeight: 500,
        fontStyle: 'normal',
        fontSize: '18px',
        lineHeight: '100%',
        letterSpacing: '0%',
        verticalAlign: 'middle'
      },
      'pb.h1.mobile': {
        fontFamily: '"Outfit", Arial, sans-serif',
        fontWeight: 800,
        fontSize: '28px',
        lineHeight: '100%',
        '@media (min-width:600px)': {
          fontSize: '36px'
        }
      },
      'pb.label.small': {
        fontSize: '12px',
        fontWeight: 600
      }
    }
  })
)

export default theme
